import React, { useState, useEffect, useCallback } from "react";
import { DashboardView } from "./DashboardView";
import { IncidentsView } from "./IncidentsView";
import { FloodsView } from "./FloodsView";
import { UsersView } from "./UsersView";
import { GroupsView } from "./GroupsView";
import { DistrictsView } from "./DistrictsView";
import { LogsView } from "./LogsView";
import { IncidentTypesView } from "./IncidentTypesView";
import { AlertsView } from "./AlertsView";

const API_BASE = "https://api.mysafeghana.com/api";

// ── Brand tokens (matches mysafeghana.com) ──────────────────────────
const C = {
  navy: "#0A1628", navy2: "#0D2137", gold: "#FCD116", red: "#CE1126",
  green: "#006B3F", white: "#fff", gray: "#777", grayLight: "#f4f6f8",
  border: "#e4e7ed", success: "#2E7D32", danger: "#C62828", warn: "#E65100",
};

const PERM_LABELS = {
  view_dashboard:     { label: "View Dashboard",       icon: "📊" },
  moderate_incidents: { label: "Moderate Incidents",   icon: "🛡️" },
  manage_alerts:      { label: "Manage Alerts",        icon: "📢" },
  manage_cameras:     { label: "Manage Cameras",       icon: "📷" },
  manage_floods:      { label: "Manage Flood Reports", icon: "🌊" },
  manage_users:       { label: "Manage Users",         icon: "👤" },
  manage_groups:      { label: "Manage Groups",        icon: "🔑" },
  manage_districts:   { label: "Manage Districts",     icon: "🗺️" },
  manage_incident_types: { label: "Manage Incident Types", icon: "🏷️" },
  view_logs:          { label: "View Error Logs",      icon: "📄" },
};

function authFetch(path, token, opts = {}) {
  return fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(opts.headers || {}),
    },
  }).then(async (r) => {
    const data = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(data.error || `Request failed (${r.status})`);
    return data;
  });
}

export default function App() {
  const [token, setToken] = useState(() => localStorage.getItem("sg_admin_token") || "");
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("dashboard");
  const [loginErr, setLoginErr] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);

  // ── Validate token / load profile on mount ──────────────────────
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    authFetch("/auth/me", token)
      .then((u) => {
        if (!u.groupId) { setMe(null); setToken(""); localStorage.removeItem("sg_admin_token"); }
        else setMe(u);
      })
      .catch(() => { setToken(""); localStorage.removeItem("sg_admin_token"); })
      .finally(() => setLoading(false));
  }, [token]);

  function handleLogin(email, password) {
    setLoginErr(""); setLoginLoading(true);
    fetch(`${API_BASE}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    })
      .then(async (r) => {
        const data = await r.json();
        if (!r.ok) throw new Error(data.error || "Login failed");
        if (!data.user.groupId) throw new Error("This account has no admin access. Contact a Super Admin.");
        localStorage.setItem("sg_admin_token", data.token);
        setToken(data.token);
        setMe(data.user);
      })
      .catch((e) => setLoginErr(e.message))
      .finally(() => setLoginLoading(false));
  }

  function handleLogout() {
    localStorage.removeItem("sg_admin_token");
    setToken(""); setMe(null);
  }

  const has = useCallback((perm) => (me?.permissions || []).includes(perm), [me]);

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: C.navy }}>
        <div style={{ color: C.gold, fontSize: 14, fontWeight: 600, letterSpacing: 1 }}>Loading…</div>
      </div>
    );
  }

  if (!token || !me) {
    return <LoginScreen onLogin={handleLogin} error={loginErr} loading={loginLoading} />;
  }

  return (
    <AdminShell me={me} token={token} view={view} setView={setView} onLogout={handleLogout} has={has} />
  );
}

// ============================================================
// LOGIN SCREEN
// ============================================================
function LoginScreen({ onLogin, error, loading }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  function submit(e) {
    e.preventDefault();
    if (!email || !password) return;
    onLogin(email, password);
  }

  return (
    <div style={{ minHeight: "100vh", background: `linear-gradient(160deg, ${C.navy} 0%, ${C.navy2} 100%)`, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      <div style={{ width: "100%", maxWidth: 380 }}>
        <div style={{ textAlign: "center", marginBottom: 28 }}>
          <div style={{ width: 52, height: 52, borderRadius: 14, background: `linear-gradient(135deg, ${C.green}, ${C.red})`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 24, marginBottom: 14 }}>🛡️</div>
          <div style={{ color: C.white, fontSize: 20, fontWeight: 700, letterSpacing: 0.3 }}>mysafeghana</div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 12, marginTop: 3, letterSpacing: 1.5, textTransform: "uppercase" }}>Admin Console</div>
        </div>

        <form onSubmit={submit} style={{ background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 16, padding: 28 }}>
          {error && (
            <div style={{ background: "rgba(198,40,40,0.15)", border: "1px solid rgba(198,40,40,0.4)", color: "#FF8A80", borderRadius: 8, padding: "10px 12px", fontSize: 12.5, marginBottom: 16 }}>
              {error}
            </div>
          )}
          <label style={{ display: "block", color: "rgba(255,255,255,0.7)", fontSize: 11.5, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Email</label>
          <input
            type="email" value={email} onChange={(e) => setEmail(e.target.value)} required autoFocus
            style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "11px 13px", color: C.white, fontSize: 14, marginBottom: 16 }}
            placeholder="you@mysafeghana.com"
          />
          <label style={{ display: "block", color: "rgba(255,255,255,0.7)", fontSize: 11.5, fontWeight: 600, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Password</label>
          <input
            type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
            style={{ width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, padding: "11px 13px", color: C.white, fontSize: 14, marginBottom: 22 }}
            placeholder="••••••••"
          />
          <button
            type="submit" disabled={loading}
            style={{ width: "100%", background: loading ? "rgba(252,209,22,0.5)" : C.gold, color: C.navy, border: "none", borderRadius: 8, padding: "12px 0", fontSize: 14.5, fontWeight: 700, cursor: loading ? "default" : "pointer" }}
          >
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>

        <div style={{ textAlign: "center", marginTop: 18, color: "rgba(255,255,255,0.35)", fontSize: 11.5 }}>
          Staff and moderator access only · sign in with your mysafeghana account
        </div>
      </div>
    </div>
  );
}

// ============================================================
// ADMIN SHELL — sidebar + content router
// ============================================================
function AdminShell({ me, token, view, setView, onLogout, has }) {
  const NAV = [
    { id: "dashboard", label: "Dashboard", icon: "📊", perm: "view_dashboard" },
    { id: "incidents", label: "Incidents", icon: "🛡️", perm: "moderate_incidents" },
    { id: "floods", label: "Flood Reports", icon: "🌊", perm: "manage_floods" },
    { id: "users", label: "Users", icon: "👤", perm: "manage_users" },
    { id: "groups", label: "Groups", icon: "🔑", perm: "manage_groups" },
    { id: "districts", label: "Districts", icon: "🗺️", perm: "manage_districts" },
    { id: "incident-types", label: "Incident Types", icon: "🏷️", perm: "manage_incident_types" },
    { id: "alerts", label: "Alerts", icon: "📢", perm: "manage_alerts" },
    { id: "logs", label: "Error Logs", icon: "📄", perm: "view_logs" },
  ].filter((n) => has(n.perm));

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: C.grayLight, fontFamily: "system-ui, -apple-system, sans-serif" }}>
      {/* Sidebar */}
      <div style={{ width: 220, background: C.navy, color: C.white, display: "flex", flexDirection: "column", flexShrink: 0 }}>
        <div style={{ padding: "20px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
            <div style={{ width: 30, height: 30, borderRadius: 8, background: `linear-gradient(135deg, ${C.green}, ${C.red})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>🛡️</div>
            <div>
              <div style={{ fontSize: 13.5, fontWeight: 700 }}>mysafeghana</div>
              <div style={{ fontSize: 9.5, color: "rgba(255,255,255,0.45)", letterSpacing: 1, textTransform: "uppercase" }}>Admin</div>
            </div>
          </div>
        </div>

        <nav style={{ flex: 1, padding: "12px 10px" }}>
          {NAV.map((n) => (
            <button
              key={n.id} onClick={() => setView(n.id)}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", marginBottom: 3,
                background: view === n.id ? "rgba(252,209,22,0.12)" : "transparent",
                color: view === n.id ? C.gold : "rgba(255,255,255,0.75)",
                border: view === n.id ? "1px solid rgba(252,209,22,0.3)" : "1px solid transparent",
                borderRadius: 8, fontSize: 13, fontWeight: view === n.id ? 600 : 500, cursor: "pointer", textAlign: "left",
              }}
            >
              <span style={{ fontSize: 15 }}>{n.icon}</span>{n.label}
            </button>
          ))}
        </nav>

        <div style={{ padding: 14, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
            <div style={{ width: 30, height: 30, borderRadius: "50%", background: C.gold, color: C.navy, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
              {(me.name || "?").split(" ").map((w) => w[0]).join("").substring(0, 2).toUpperCase()}
            </div>
            <div style={{ overflow: "hidden" }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{me.name}</div>
              <div style={{ fontSize: 10.5, color: "rgba(255,255,255,0.45)" }}>{me.groupName}</div>
            </div>
          </div>
          <button onClick={onLogout} style={{ width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.7)", borderRadius: 7, padding: "8px 0", fontSize: 12, cursor: "pointer" }}>
            Sign Out
          </button>
        </div>
      </div>

      {/* Main content */}
      <div style={{ flex: 1, overflowY: "auto", padding: 28 }}>
        {view === "dashboard" && has("view_dashboard") && <DashboardView token={token} />}
        {view === "incidents" && has("moderate_incidents") && <IncidentsView token={token} />}
        {view === "floods" && has("manage_floods") && <FloodsView token={token} />}
        {view === "users" && has("manage_users") && <UsersView token={token} />}
        {view === "groups" && has("manage_groups") && <GroupsView token={token} />}
        {view === "districts" && has("manage_districts") && <DistrictsView token={token} />}
        {view === "incident-types" && has("manage_incident_types") && <IncidentTypesView token={token} />}
        {view === "alerts" && has("manage_alerts") && <AlertsView token={token} />}
        {view === "logs" && has("view_logs") && <LogsView token={token} />}
      </div>
    </div>
  );
}

export { authFetch, C, PERM_LABELS, API_BASE };
